<template>
    <div class="downtime-event-card">
        <div class="downtime-event-card__header" @click="expanded = !expanded">
            <div class="d-flex align-items-center gap-3">
                <CBadge color="warning" text-color="dark">Needs Review</CBadge>
                <div>
                    <strong>{{ formatDate(downtimeEvent.date) }}</strong> —
                    {{ downtimeEvent.cycle_pattern }}
                </div>
                <div class="ms-auto text-muted">
                    {{ formatDuration(downtimeEvent.duration_seconds) }}
                </div>
                <icon :icon="expanded ? 'cil-chevron-top' : 'cil-chevron-bottom'" />
            </div>
        </div>

        <div v-if="expanded" class="downtime-event-card__body">
            <div class="reason-box mb-3">
                <strong class="small">Why does this need review?</strong>
                <p class="mt-1 mb-0">{{ downtimeEvent.reason }}</p>
                <div class="small mt-1 text-muted">
                    Classification: <strong>{{ downtimeEvent.classification }}</strong> · Confidence:
                    <strong>{{ downtimeEvent.auto_confidence }}</strong> · Duration:
                    <strong>{{ formatDuration(downtimeEvent.duration_seconds) }}</strong>
                </div>
            </div>
            <div class="mb-3">
                <strong class="small">Event Timeline</strong>
                <div class="event-timeline mt-2">
                    <div v-for="(evt, i) in parsedEvents" :key="i" class="event-row small">
                        <span class="event-time font-monospace">{{ evt.event_time }}</span>
                        <CBadge :color="eventColor(evt.event)" class="event-badge">{{ evt.event }}</CBadge>
                        <span class="text-muted">{{ evt.event_detail || '' }}</span>
                    </div>
                </div>
            </div>

            <div class="row g-3">
                <div class="col-md-4">
                    <CFormLabel>Category</CFormLabel>
                    <CFormSelect v-model="form.new_category" size="sm">
                        <option value="included">Included (counts as downtime)</option>
                        <option value="bag_jam">Bag Jam (separate tracking)</option>
                        <option value="excluded">Excluded (no impact)</option>
                        <option value="none">None (not an incident)</option>
                    </CFormSelect>
                </div>
                <div class="col-md-8">
                    <CFormLabel>Notes</CFormLabel>
                    <CFormInput v-model="form.notes" placeholder="Reason for this decision..." size="sm" />
                </div>
            </div>

            <div class="d-flex justify-content-end mt-3">
                <CButton :disabled="submitting" color="primary" size="sm" @click="submitReview">
                    <CSpinner v-if="submitting" size="sm" />
                    Save Review
                </CButton>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { CBadge, CButton, CFormLabel, CFormSelect, CFormInput, CSpinner } from '@coreui/vue';
import { $http } from '@/bootstrap.js';

const props = defineProps({
    downtimeEvent: { type: Object, required: true },
});

const emit = defineEmits(['reviewed']);

const expanded = ref(false);
const submitting = ref(false);

const form = ref({
    new_category: 'included',
    notes: '',
});

const parsedEvents = computed(() => {
    if (Array.isArray(props.downtimeEvent.raw_events_json)) {
        return props.downtimeEvent.raw_events_json;
    }
    try {
        return JSON.parse(props.downtimeEvent.raw_events_json);
    } catch {
        return [];
    }
});

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDuration(seconds) {
    if (!seconds) return '00:00:00';
    const s = Math.round(seconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function eventColor(event) {
    const colors = {
        OPERATIONAL: 'success',
        ONLINE: 'info',
        'SYSTEM-ERROR': 'danger',
        'BAG JAM': 'warning',
        SHUTDOWN: 'danger',
        'FAULT-RESET': 'info',
        STARTUP: 'primary',
        OFFLINE: 'secondary',
        'SW START': 'primary',
        SERVICE: 'secondary',
        FAULTED: 'danger',
    };
    return colors[event] ?? 'secondary';
}

async function submitReview() {
    submitting.value = true;
    try {
        await $http.post(route('eds-operations.downtime-events.review', { downtimeEvent: props.downtimeEvent.id }), form.value);
        emit('reviewed', props.downtimeEvent.id);
    } catch (error) {
        console.error('Error submitting review:', error);
    } finally {
        submitting.value = false;
    }
}
</script>

<style scoped>
.downtime-event-card {
    border: 1px solid var(--cui-border-color, #d8dbe0);
    border-radius: 0.375rem;
    overflow: hidden;
}

.downtime-event-card__header {
    padding: 0.75rem 1rem;
    cursor: pointer;
    background: var(--cui-tertiary-bg, #f8f9fa);
}

.downtime-event-card__header:hover {
    background: var(--cui-secondary-bg, #ebedef);
}

.downtime-event-card__body {
    padding: 1rem;
    border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

.event-timeline {
    max-height: 200px;
    overflow-y: auto;
}

.event-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0;
}

.reason-box {
    background: var(--cui-tertiary-bg, #f8f9fa);
    border-left: 3px solid var(--cui-warning, #f9b115);
    padding: 0.75rem 1rem;
    border-radius: 0 0.25rem 0.25rem 0;
}

.event-time {
    flex-shrink: 0;
    width: 70px;
    color: var(--cui-text-muted);
}

.event-badge {
    flex-shrink: 0;
    min-width: 90px;
    text-align: center;
    font-size: 0.7rem;
}
</style>
